import { useCallback } from 'react';

import { getLocation } from '@web-platform/shared-universal-redux/src/state';
import { useStore } from 'react-redux';
import observer from 'saddlebag-observer';

import type { buildViewPageContext } from '@web-platform/shared-universal-tracking/src/funnelEvents/viewEvent';
import type { State } from '@web-platform/shared-universal-types/src/redux/banana/state/state-banana-types';

type SpaPageViewedArgs = {
  viewGuid: string;
  requestId: string;
  pageContext: ReturnType<typeof buildViewPageContext>;
  previousName?: string;
  referrerId?: string;
};

/**
 * Publishes the `trackolding-spa-page-viewed` funnel view event.
 *
 * `virtualReferrer` is intentionally NOT accepted from the caller: it is read live from the
 * Redux store at publish time. Reading it from a component render closure can capture the value
 * prior to navigation (occurring before `LOCATION_CHANGED`). It is safer to capture the referrer
 * value at the same time the event is to be published.
 *
 * This does NOT decide *when* to publish — callers are still responsible for firing at the
 * right time but couples the reading of the value to the publishing of the event.
 *
 * Usage:
 * ```ts
 * const publishSpaPageViewed = usePublishSpaPageViewed();
 * publishSpaPageViewed({ pageContext, previousName, referrerId, requestId, viewGuid });
 *```
 */

export const usePublishSpaPageViewed = () => {
  const { getState } = useStore<State>();

  return useCallback(
    ({ pageContext, previousName, referrerId, requestId, viewGuid }: SpaPageViewedArgs) => {
      const { virtualReferrer } = getLocation(getState());

      observer.publish('trackolding-spa-page-viewed', {
        previousName,
        referrerId,
        viewGuid,
        requestId,
        pageContext,
        virtualReferrer,
      });
    },
    [getState],
  );
};
